/**
 * Which nav entry is the current one.
 *
 * 🔴 EXTRACTED BECAUSE `NavLink` GOT IT WRONG AND NOBODY NOTICED. Jim,
 * 2026-09-14: *"When I click on 'Lots' it just redirects me to Sales in the nav
 * and Sales shows the Lots."*
 *
 * `/live` resolves the open sale and lands on `/auctions/:slug/live`. The
 * Auctions entry points at `/auctions` with no `end`, so React Router's own
 * prefix match lit it up — the bidder clicked Lots, the page showed lots, and
 * the nav said Auctions. The Lots entry could never be current, because after
 * the redirect the URL no longer starts with `/live`.
 *
 * A prefix match cannot express this: the two entries own overlapping paths and
 * the deeper one has to win. So the rule is written out, per entry, and tested
 * at the boundaries rather than inferred from the `to` value.
 *
 * The bidder-web CSS keys the current entry off `aria-current="page"` — not an
 * `.active` class — so the caller sets that attribute from this function.
 */
export type NavMatch = (pathname: string) => boolean;

/** `/auctions/:slug/live` — the merged Lots page for one sale. */
const SALE_LIVE = /^\/auctions\/[^/]+\/live\/?$/;
/** `/auctions/:slug/lots/:n` — a lot, always reached FROM the Lots page. */
const SALE_LOT = /^\/auctions\/[^/]+\/lots\//;
/** `/auctions/:slug` — one sale's own page. Belongs to Auctions, not Lots. */
const SALE_DETAIL = /^\/auctions\/[^/]+\/?$/;

/**
 * Lots owns the merged page and everything reached from it.
 *
 * `/browse` and `/live` are both resolvers that land on the merged page; they
 * are listed so the entry is already current during the moment before the
 * redirect completes, rather than flashing no selection at all.
 */
export const isLotsCurrent: NavMatch = (p) =>
  p === '/live' || p === '/browse' || SALE_LIVE.test(p) || SALE_LOT.test(p);

/**
 * Auctions owns the list and a single sale's page — but NOT that sale's lots.
 *
 * A completed sale has a results page at `/auctions/:slug` and no live lots, so
 * it genuinely belongs here; an OPEN sale at the same path redirects through to
 * the merged page, at which point `isLotsCurrent` takes over.
 */
export const isAuctionsCurrent: NavMatch = (p) =>
  !isLotsCurrent(p) && (p === '/auctions' || SALE_DETAIL.test(p));
